import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useRouteRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import EditIcon from '@material-ui/icons/Edit';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  AI_ENGINE_CAPABILITIES,
  type AIEngineName,
} from '@internal/spassu-code-contract';
import { spassuCodeApiRef } from '../../api';
import {
  AiEngineCatalogEntry,
  AzureBranch,
  AzureProject,
  AzureRepository,
  AzureWorkItemSummary,
  AzureWorkItemDetail,
  Issue,
  Repository,
  RepositoryOwner,
  ScmProviderDescriptor,
  SearchWorkItemsFilters,
} from '../../api/types';
import { ProviderSelector } from './ProviderSelector';
import { RepoHierarchyPicker, RepoHierarchyValue } from './RepoHierarchyPicker';
import { IssuePicker } from './IssuePicker';
import { activityDetailRouteRef } from '../../routes';
import { useProjectRole } from '../../hooks';
import { normalizeRouteRefPath } from '../../utils/routes';
import { WorkItemPicker, WorkItemPreview } from '../WorkItemPicker';

export interface CreateActivityDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

// D-GHC-5 — seletor de engine. Ordem e rótulos fixos por engine; a
// disponibilidade (credencial/worker) vem do catálogo GET /ai-engines.
const AI_ENGINE_OPTION_ORDER: AIEngineName[] = [
  'claude-code',
  'codex',
  'github-copilot',
];

const AI_ENGINE_OPTION_LABELS: Record<AIEngineName, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  'github-copilot': 'GitHub Copilot',
};

/**
 * Motivo pelo qual a engine está bloqueada no seletor (D-GHC-5 +
 * disponibilidade-runtime-engines), ou `null` quando utilizável. Só bloqueia
 * com conhecimento POSITIVO (worker conectado que reportou o problema);
 * estado desconhecido permanece fail-open.
 */
function engineBlockedReason(entry?: AiEngineCatalogEntry): string | null {
  if (!entry) return null;
  if (entry.supportedByWorker === false) {
    return 'O worker conectado não suporta esta engine — atualize o worker para usá-la.';
  }
  if (entry.authStatus?.state !== 'known') return null;
  if (entry.authStatus.runtimeAvailable === false) {
    return (
      entry.authStatus.runtimeDiagnostic ??
      'Runtime da engine não encontrado na máquina do worker — instale pelo app do worker (Desktop).'
    );
  }
  if (!entry.authStatus.authenticated) {
    return (
      entry.authStatus.diagnostic ??
      'Engine sem credencial configurada na máquina do worker.'
    );
  }
  return null;
}

function isEngineOptionDisabled(entry?: AiEngineCatalogEntry): boolean {
  return engineBlockedReason(entry) !== null;
}

/**
 * Texto de apoio do seletor para a engine em foco: motivo + ação quando
 * indisponível (D-GHC-5); senão o alcance de fases derivado das capabilities.
 */
function engineHelperText(
  engine: AIEngineName,
  entry?: AiEngineCatalogEntry,
): string {
  const blockedReason = engineBlockedReason(entry);
  if (blockedReason) {
    return blockedReason;
  }
  const supportsSkills =
    entry?.capabilities.supportsSkillsPlugin ??
    AI_ENGINE_CAPABILITIES[engine].supportsSkillsPlugin;
  const base = supportsSkills
    ? 'Pipeline completo suportado.'
    : 'Fases "Dividindo Tasks", "Executando" e "Pronto" ficam bloqueadas nesta versão (sem skills).';
  if (entry?.authStatus?.state === 'unknown') {
    const reason =
      entry.authStatus.reason === 'worker_disconnected'
        ? 'worker desconectado'
        : 'worker desatualizado';
    return `${base} Credencial não verificada (${reason}).`;
  }
  return base;
}

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  modeToggle: {
    marginBottom: theme.spacing(2),
  },
  wiSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  wiChip: {
    marginBottom: theme.spacing(1),
  },
  wiPreview: {
    marginTop: theme.spacing(1.5),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

const TITLE_MAX_LENGTH = 500;

// Mesmo intervalo do WorkerStatusIndicator: o alerta de worker desconectado
// some sozinho quando o usuário conecta o Worker Desktop com o dialog aberto.
const WORKER_STATUS_POLL_MS = 15_000;

type DialogMode = 'blank' | 'work-item';

export const CreateActivityDialog = ({
  open,
  onClose,
  onCreated,
}: CreateActivityDialogProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const config = useApi(configApiRef);
  const navigate = useNavigate();
  const activityDetailRoute = useRouteRef(activityDetailRouteRef);
  const {
    isAdmin,
    hasPermission,
    getRolesForProject,
    roles: accessibleProjects,
    loading: rbacLoading,
    error: rbacError,
  } = useProjectRole();

  // Fluxo agnóstico (GitHub etc.): mesma restrição que o fluxo Azure aplica em
  // loadProjects/loadRepos. No esquema RBAC desses providers, project_key =
  // owner e repository_key = nome do repo. Enquanto o RBAC carrega (ou
  // falhou) não filtra — mesmo fallback de compatibilidade do fluxo Azure; o
  // gate do backend em POST /activities continua sendo a barreira real.
  const rbacUnavailable = rbacLoading || !!rbacError;
  const filterAgnosticOwners = useCallback(
    (owner: RepositoryOwner) =>
      rbacUnavailable ||
      isAdmin ||
      hasPermission(owner.name, 'activity.create'),
    [rbacUnavailable, isAdmin, hasPermission],
  );
  const filterAgnosticRepositories = useCallback(
    (repo: Repository) => {
      if (rbacUnavailable || isAdmin) return true;
      const access =
        accessibleProjects.find(
          p =>
            p.projectKey === repo.owner &&
            (!p.provider || p.provider === repo.providerId),
        ) ?? accessibleProjects.find(p => p.projectKey === '*');
      if (!access || access.allRepositories) return true;
      return access.repositories.includes(repo.name);
    },
    [rbacUnavailable, isAdmin, accessibleProjects],
  );

  const azureBoardsEnabled =
    config.getOptionalBoolean('spassuCode.features.azureBoards') ?? false;

  const [projects, setProjects] = useState<AzureProject[]>([]);
  const [repositories, setRepositories] = useState<AzureRepository[]>([]);
  const [branches, setBranches] = useState<AzureBranch[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // D2 — seleção de engine de IA. Sem default: o usuário escolhe
  // explicitamente (seleção obrigatória na validação). O default
  // claude-code fica só no backend (migration 045), para fluxos que não
  // enviam o campo.
  const [selectedAiEngine, setSelectedAiEngine] = useState<AIEngineName | ''>(
    '',
  );
  // DT-06/D-GHC-5 — catálogo do backend com status de credencial por engine.
  // null = ainda não carregado/falhou: o Select degrada para as opções
  // estáticas (todas habilitadas), preservando o comportamento anterior.
  const [aiEngines, setAiEngines] = useState<AiEngineCatalogEntry[] | null>(
    null,
  );

  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // RF5/D-RTE-5 — a via Frontier Web dispara o clone automático via worker na
  // criação, então exige worker conectado. `null` = status ainda não
  // carregado (não bloqueia).
  const [workerConnected, setWorkerConnected] = useState<boolean | null>(null);

  // Multi-provider state (Tarefa 5+7). Azure flow continues unchanged — when
  // the selected provider is non-Azure we render the agnostic
  // RepoHierarchyPicker + IssuePicker pair instead.
  const [scmProviders, setScmProviders] = useState<ScmProviderDescriptor[]>([]);
  const [selectedScmProvider, setSelectedScmProvider] =
    useState<ScmProviderDescriptor | null>(null);
  const [agnosticHierarchy, setAgnosticHierarchy] =
    useState<RepoHierarchyValue>({
      owner: null,
      project: null,
      repository: null,
      branch: null,
    });
  const [agnosticIssue, setAgnosticIssue] = useState<Issue | null>(null);

  // Provider escolhido pelo usuário dirige a UI: Azure mantém o fluxo legado
  // (Work Items + project selector), demais providers usam o fluxo agnóstico
  // (RepoHierarchyPicker + IssuePicker). Sem dependência de feature flag —
  // Azure é um provider entre vários, não obrigatório.
  const isAzureFlow = selectedScmProvider?.id === 'azure-devops';
  const isAgnosticProvider =
    !!selectedScmProvider && selectedScmProvider.id !== 'azure-devops';

  // Work Item state
  const [mode, setMode] = useState<DialogMode>('work-item');
  const [selectedWorkItem, setSelectedWorkItem] =
    useState<AzureWorkItemDetail | null>(null);
  const [titleEditedByUser, setTitleEditedByUser] = useState(false);

  // Role-based defaults
  const projectRoles = useMemo(
    () => (selectedProject ? getRolesForProject(selectedProject) : []),
    [selectedProject, getRolesForProject],
  );
  const canCreateBlank =
    isAdmin ||
    projectRoles.includes('gestor') ||
    projectRoles.includes('analista');

  const wiInitialFilters = useMemo<Partial<SearchWorkItemsFilters>>(() => {
    if (canCreateBlank) return {};
    // Desenvolvedor: default @me + workable states
    return { assignedToMe: true };
  }, [canCreateBlank]);

  // Load Azure projects — só quando o fluxo Azure está EXPLICITAMENTE
  // selecionado. Antes, a condição era "não-agnóstico", o que incluía o
  // estado inicial `selectedScmProvider === null` (antes do listScmProviders
  // resolver), provocando uma chamada listProjects() especulativa que falhava
  // quando Azure não estava configurado e deixava um banner de erro stale.
  // Em fluxo agnóstico (GitHub etc.) o `RepoHierarchyPicker` resolve
  // owners/repos via `/scm/*`.
  useEffect(() => {
    if (!open) return undefined;
    if (!isAzureFlow) {
      setProjects([]);
      return undefined;
    }
    let cancelled = false;

    const loadProjects = async () => {
      setLoadingProjects(true);
      setError(undefined);
      try {
        const result = await api.listProjects();
        if (!cancelled) {
          // Filter by RBAC: only projects where user can create activities
          // Fallback: show all if RBAC is loading/errored (backwards compat)
          const filtered =
            rbacLoading || rbacError
              ? result
              : result.filter(
                  p => isAdmin || hasPermission(p.name, 'activity.create'),
                );
          setProjects(filtered);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar projetos: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [open, api, isAzureFlow, rbacLoading, rbacError, isAdmin, hasPermission]);

  // Load registered SCM providers — used by ProviderSelector. The legacy
  // listProjects() call above stays for the existing Azure-only flow.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api
      .listScmProviders()
      .then(items => {
        if (cancelled) return;
        setScmProviders(items);
        if (items.length > 0 && !selectedScmProvider) {
          // Preferir Azure DevOps quando registrado — preserva o fluxo legado
          // como default e evita o usuário cair no fluxo agnóstico só porque
          // o backend retornou providers em ordem distinta. Se Azure não
          // estiver registrado, cai no primeiro item.
          const preferred =
            items.find(p => p.id === 'azure-devops') ?? items[0];
          setSelectedScmProvider(preferred);
        }
      })
      .catch(() => {
        // Silent — multi-provider is opt-in. Failures fall back to the
        // legacy Azure-only flow that's already loaded by `listProjects`.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api]);

  // RF5/D-RTE-5 — status do worker enquanto o dialog está aberto. Falha na
  // consulta conta como desconectado (mesma leitura do WorkerStatusIndicator).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const status = await api.getWorkerStatus();
        if (!cancelled) setWorkerConnected(status.connected);
      } catch {
        if (!cancelled) setWorkerConnected(false);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, WORKER_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, api]);

  // DT-06 — catálogo de engines com credencial/disponibilidade por engine.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api
      .getAiEngines()
      .then(res => {
        if (!cancelled) setAiEngines(res.engines);
      })
      .catch(() => {
        // Silent — sem o catálogo o Select degrada para as opções estáticas.
      });
    return () => {
      cancelled = true;
    };
  }, [open, api]);

  // Load repos
  useEffect(() => {
    if (!selectedProject) {
      setRepositories([]);
      setSelectedRepo('');
      return undefined;
    }
    let cancelled = false;

    const loadRepos = async () => {
      setLoadingRepos(true);
      setSelectedRepo('');
      setRepositories([]);
      try {
        const result = await api.listRepositories(selectedProject);
        if (!cancelled) {
          // Filter repos by RBAC: if user has repo-specific permissions, show only those
          if (!rbacLoading && !rbacError && !isAdmin) {
            const myRoles = await api.getMyRoles();
            const projectAccess = myRoles.data.find(
              p => p.projectKey === selectedProject || p.projectKey === '*',
            );
            if (
              projectAccess &&
              !projectAccess.allRepositories &&
              projectAccess.repositories.length > 0
            ) {
              const allowed = new Set(projectAccess.repositories);
              setRepositories(result.filter(r => allowed.has(r.name)));
            } else {
              setRepositories(result);
            }
          } else {
            setRepositories(result);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar repositórios: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingRepos(false);
        }
      }
    };

    loadRepos();
    return () => {
      cancelled = true;
    };
  }, [selectedProject, api, rbacLoading, rbacError, isAdmin]);

  // Load branches
  useEffect(() => {
    if (!selectedRepo || !selectedProject) {
      setBranches([]);
      setSelectedBranch('');
      return undefined;
    }
    const repoObj = repositories.find(r => r.name === selectedRepo);
    if (!repoObj) {
      setBranches([]);
      setSelectedBranch('');
      return undefined;
    }
    let cancelled = false;

    const loadBranches = async () => {
      setLoadingBranches(true);
      setSelectedBranch('');
      setBranches([]);
      try {
        const result = await api.listBranches(selectedProject, repoObj.id);
        if (!cancelled) {
          setBranches(result);
          // Auto-select default branch
          const defaultBranch = result.find(b => b.isDefault);
          if (defaultBranch) {
            setSelectedBranch(defaultBranch.name);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar branches: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingBranches(false);
        }
      }
    };

    loadBranches();
    return () => {
      cancelled = true;
    };
  }, [selectedRepo, selectedProject, repositories, api]);

  // Reset WI state when project changes
  useEffect(() => {
    setSelectedWorkItem(null);
    setTitleEditedByUser(false);
    setMode(azureBoardsEnabled ? 'work-item' : 'blank');
  }, [selectedProject, azureBoardsEnabled]);

  const handleSelectWi = useCallback(
    async (summary: AzureWorkItemSummary) => {
      try {
        const detail = await api.getWorkItem(selectedProject, summary.id);
        setSelectedWorkItem(detail);
        // Pre-fill title only if user hasn't manually edited it.
        if (!titleEditedByUser) {
          setTitle(detail.title);
        }
        const descParts: string[] = [];
        if (detail.descriptionText) {
          descParts.push(detail.descriptionText);
        } else if (detail.reproStepsText) {
          descParts.push(detail.reproStepsText);
        }
        if (detail.acceptanceCriteriaText) {
          descParts.push(
            `\n\n## Critérios de Aceitação\n\n${detail.acceptanceCriteriaText}`,
          );
        }
        if (descParts.length > 0) setDescription(descParts.join(''));
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg === 'WORK_ITEM_NOT_FOUND') {
          setError('Work Item não encontrado. Pode ter sido removido.');
        } else if (msg === 'AZURE_PERMISSION_DENIED') {
          setError(
            'Permissão negada ao acessar o Work Item. Verifique o escopo do PAT.',
          );
        } else {
          setError(`Erro ao carregar Work Item: ${msg}`);
        }
      }
    },
    [api, selectedProject, titleEditedByUser],
  );

  const clearWorkItem = useCallback(() => {
    setSelectedWorkItem(null);
    if (!titleEditedByUser) setTitle('');
    setDescription('');
  }, [titleEditedByUser]);

  const handleModeChange = useCallback(
    (val: DialogMode) => {
      if (val === 'blank') {
        setSelectedWorkItem(null);
        if (!titleEditedByUser) setTitle('');
        setDescription('');
      }
      setMode(val);
    },
    [titleEditedByUser],
  );

  const resetForm = useCallback(() => {
    setSelectedProject('');
    setSelectedRepo('');
    setSelectedBranch('');
    setTitle('');
    setDescription('');
    setSelectedAiEngine('');
    setError(undefined);
    setFieldErrors({});
    setProjects([]);
    setRepositories([]);
    setBranches([]);
    setSelectedWorkItem(null);
    setTitleEditedByUser(false);
    setMode(azureBoardsEnabled ? 'work-item' : 'blank');
    setAgnosticHierarchy({
      owner: null,
      project: null,
      repository: null,
      branch: null,
    });
    setAgnosticIssue(null);
  }, [azureBoardsEnabled]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    onClose();
  }, [submitting, resetForm, onClose]);

  const handleDialogClose = useCallback(
    (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
      handleClose();
    },
    [handleClose],
  );

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (isAgnosticProvider) {
      if (!agnosticHierarchy.owner) {
        errors.owner = `Selecione um(a) ${
          selectedScmProvider!.hierarchy.ownerLabel
        }`;
      }
      if (
        selectedScmProvider!.hierarchy.hasProject &&
        !agnosticHierarchy.project
      ) {
        errors.project = `Selecione um(a) ${
          selectedScmProvider!.hierarchy.projectLabel ?? 'Project'
        }`;
      }
      if (!agnosticHierarchy.repository) {
        errors.repo = 'Selecione um repositório';
      }
    } else {
      if (!selectedProject) {
        errors.project = 'Selecione um projeto';
      }
      if (!selectedRepo) {
        errors.repo = 'Selecione um repositório';
      }
    }
    if (!title.trim()) {
      errors.title = 'Título é obrigatório';
    } else if (title.trim().length > TITLE_MAX_LENGTH) {
      errors.title = `Título deve ter no máximo ${TITLE_MAX_LENGTH} caracteres`;
    }
    if (!selectedAiEngine) {
      errors.aiEngine = 'Selecione um harness';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    isAgnosticProvider,
    agnosticHierarchy,
    selectedScmProvider,
    selectedProject,
    selectedRepo,
    selectedAiEngine,
    title,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setSubmitting(true);
    setError(undefined);
    try {
      // validate() garante seleção — o cast só remove o '' do union.
      const aiEngine = selectedAiEngine as AIEngineName;
      const activity = await api.createActivity(
        isAgnosticProvider
          ? {
              title: title.trim(),
              description: description || undefined,
              workPath: 'frontier-web',
              provider: selectedScmProvider!.id,
              repoOwner: agnosticHierarchy.owner!.id,
              repoProject: agnosticHierarchy.project?.name ?? null,
              repoName: agnosticHierarchy.repository!.name,
              branch: agnosticHierarchy.branch?.name || undefined,
              issueExternalId: agnosticIssue?.externalId,
              issueType: agnosticIssue?.type,
              issueTitle: agnosticIssue?.title,
              issueState: agnosticIssue?.state,
              issueUrl: agnosticIssue?.url,
              aiEngine,
            }
          : {
              title: title.trim(),
              description: description || undefined,
              azureProject: selectedProject,
              azureRepository: selectedRepo,
              branch: selectedBranch || undefined,
              workPath: 'frontier-web',
              azureWorkItemId: selectedWorkItem?.id,
              azureWorkItemType: selectedWorkItem?.type,
              azureWorkItemUrl: selectedWorkItem?.url,
              azureWorkItemTitle: selectedWorkItem?.title,
              azureWorkItemState: selectedWorkItem?.state,
              aiEngine,
            },
      );

      onCreated?.();
      resetForm();
      onClose();
      navigate(
        normalizeRouteRefPath(activityDetailRoute({ activityId: activity.id })),
      );
    } catch (e) {
      setError(
        `Erro ao criar atividade: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- T-24a preserva o conjunto legado sem mudar o comportamento.
  }, [
    validate,
    api,
    title,
    description,
    selectedProject,
    selectedRepo,
    selectedBranch,
    selectedWorkItem,
    // selectedAiEngine é OBRIGATÓRIO aqui: sem ele o closure do useCallback
    // captura o valor inicial ('') e a atividade nasce sem engine, mesmo o
    // usuário escolhendo uma no dropdown (stale closure).
    selectedAiEngine,
    isAgnosticProvider,
    selectedScmProvider,
    agnosticHierarchy,
    agnosticIssue,
    onCreated,
    resetForm,
    onClose,
    navigate,
    activityDetailRoute,
  ]);

  // RF5/D-RTE-5 — bloqueio de criação: a criação exige o worker conectado
  // (clone automático).
  const workerBlocksCreation = workerConnected === false;

  // disponibilidade-runtime-engines: catálogo carregado e TODAS as engines
  // bloqueadas (sem runtime/credencial/suporte no worker) — o seletor vira um
  // beco sem saída, então o campo avisa o motivo real no lugar do erro de
  // validação. Sem catálogo (null), permanece o fail-open padrão.
  const noEngineAvailable =
    aiEngines !== null &&
    AI_ENGINE_OPTION_ORDER.every(engineName =>
      isEngineOptionDisabled(aiEngines.find(e => e.name === engineName)),
    );

  const showWiSection =
    azureBoardsEnabled && selectedProject && mode === 'work-item';
  const dialogMaxWidth = showWiSection ? ('md' as const) : ('sm' as const);

  if (submitting) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent>
          <div className={classes.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="h6">Criando atividade...</Typography>
            <Typography variant="body2" color="textSecondary">
              Clonando repositório, isso pode levar alguns segundos.
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={dialogMaxWidth}
      fullWidth
    >
      <DialogTitle>Nova Atividade</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className={classes.field}>
            {error}
          </Alert>
        )}

        {workerBlocksCreation && (
          <Alert severity="warning" className={classes.field}>
            Worker não conectado. Abra o aplicativo Frontier - Spassu Code
            (Worker Desktop), conecte o worker e tente novamente — a via
            Frontier Web precisa dele para clonar o repositório na criação.
          </Alert>
        )}

        {/*
         * ProviderSelector renders silently quando há um único provider
         * registrado; com 2+ providers, surge o dropdown. A escolha aqui dirige
         * todo o dialog: Azure mantém o fluxo legado (project + Work Items),
         * demais providers entram no fluxo agnóstico (RepoHierarchyPicker +
         * IssuePicker). Sem feature flag — registro do provider no backend é
         * a única condição.
         */}
        <ProviderSelector
          providers={scmProviders}
          value={selectedScmProvider?.id ?? null}
          onChange={next => {
            setSelectedScmProvider(next);
            // Erro carregado pelo provider anterior (ex.: listProjects falhando
            // no fluxo Azure) não deve persistir após o usuário trocar de
            // provider — confunde quem está só explorando opções.
            setError(undefined);
          }}
        />

        {/* Agnostic flow (GitHub etc.). The Azure-specific blocks below are
         * skipped — the registry-backed `RepoHierarchyPicker` + `IssuePicker`
         * cover the same surface using provider-aware endpoints. */}
        {isAgnosticProvider && selectedScmProvider && (
          <>
            <RepoHierarchyPicker
              provider={selectedScmProvider}
              value={agnosticHierarchy}
              filterOwners={filterAgnosticOwners}
              filterRepositories={filterAgnosticRepositories}
              onChange={next => {
                setAgnosticHierarchy(next);
                setFieldErrors(prev => ({
                  ...prev,
                  owner: '',
                  project: '',
                  repo: '',
                }));
              }}
            />
            {fieldErrors.owner && (
              <FormHelperText error>{fieldErrors.owner}</FormHelperText>
            )}
            {fieldErrors.project && (
              <FormHelperText error>{fieldErrors.project}</FormHelperText>
            )}
            {fieldErrors.repo && (
              <FormHelperText error>{fieldErrors.repo}</FormHelperText>
            )}

            {agnosticHierarchy.repository && (
              <div className={classes.wiSection}>
                <IssuePicker
                  provider={selectedScmProvider.id}
                  scope={{
                    owner: agnosticHierarchy.owner!.id,
                    project: agnosticHierarchy.project?.name,
                    repo: agnosticHierarchy.repository.name,
                  }}
                  selected={agnosticIssue}
                  onSelect={issue => {
                    setAgnosticIssue(issue);
                    if (issue && !titleEditedByUser) setTitle(issue.title);
                    if (issue?.description) setDescription(issue.description);
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Project selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            error={!!fieldErrors.project}
          >
            <InputLabel id="create-project-label">
              Projeto Azure DevOps
            </InputLabel>
            <Select
              labelId="create-project-label"
              label="Projeto Azure DevOps"
              value={selectedProject}
              onChange={e => {
                setSelectedProject(e.target.value as string);
                setFieldErrors(prev => ({ ...prev, project: '' }));
              }}
              disabled={loadingProjects}
            >
              {loadingProjects && (
                <MenuItem value="" disabled>
                  Carregando projetos...
                </MenuItem>
              )}
              {!loadingProjects &&
                projects.map(p => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.name}
                  </MenuItem>
                ))}
            </Select>
            {fieldErrors.project && (
              <FormHelperText>{fieldErrors.project}</FormHelperText>
            )}
          </FormControl>
        )}

        {/* Mode toggle (only if feature flag ON + project selected + canCreateBlank) */}
        {!isAgnosticProvider &&
          azureBoardsEnabled &&
          selectedProject &&
          canCreateBlank && (
            <div className={classes.modeToggle}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_e: unknown, val: string | null) => {
                  if (val) handleModeChange(val as DialogMode);
                }}
                size="small"
              >
                <ToggleButton value="work-item">
                  <LinkIcon fontSize="small" style={{ marginRight: 4 }} />
                  Vincular Card do Board
                </ToggleButton>
                <ToggleButton value="blank">
                  <EditIcon fontSize="small" style={{ marginRight: 4 }} />
                  Nova Atividade em Branco
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          )}

        {/* Work Item section */}
        {!isAgnosticProvider && showWiSection && (
          <div className={classes.wiSection}>
            {selectedWorkItem && (
              <Chip
                icon={<LinkIcon />}
                label={`#${selectedWorkItem.id} — ${selectedWorkItem.type}: ${selectedWorkItem.title}`}
                onDelete={clearWorkItem}
                color="secondary"
                className={classes.wiChip}
              />
            )}

            <WorkItemPicker
              projectId={selectedProject}
              onSelect={handleSelectWi}
              selectedId={selectedWorkItem?.id}
              initialFilters={wiInitialFilters}
              lockAssignedToMe={!canCreateBlank}
              renderEmpty={filters => (
                <div className={classes.emptyState}>
                  {filters.assignedToMe ? (
                    <>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Nenhum Work Item atribuído a você neste projeto.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Peça ao seu Analista de Requisitos ou PO para atribuir
                        um item do backlog.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum Work Item encontrado. Refine os filtros.
                    </Typography>
                  )}
                </div>
              )}
            />
            {selectedWorkItem && (
              <div className={classes.wiPreview}>
                <WorkItemPreview
                  projectId={selectedProject}
                  workItemId={selectedWorkItem.id}
                />
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />
          </div>
        )}

        {/* Repo selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            error={!!fieldErrors.repo}
          >
            <InputLabel id="create-repo-label">Repositório</InputLabel>
            <Select
              labelId="create-repo-label"
              label="Repositório"
              value={selectedRepo}
              onChange={e => {
                setSelectedRepo(e.target.value as string);
                setFieldErrors(prev => ({ ...prev, repo: '' }));
              }}
              disabled={!selectedProject || loadingRepos}
            >
              {loadingRepos && (
                <MenuItem value="" disabled>
                  Carregando repositórios...
                </MenuItem>
              )}
              {!loadingRepos &&
                repositories.map(r => (
                  <MenuItem key={r.id} value={r.name}>
                    {r.name}
                  </MenuItem>
                ))}
            </Select>
            {fieldErrors.repo && (
              <FormHelperText>{fieldErrors.repo}</FormHelperText>
            )}
          </FormControl>
        )}

        {/* Branch selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
          >
            <InputLabel id="create-branch-label">Branch</InputLabel>
            <Select
              labelId="create-branch-label"
              label="Branch"
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value as string)}
              disabled={!selectedRepo || loadingBranches}
            >
              {loadingBranches && (
                <MenuItem value="" disabled>
                  Carregando branches...
                </MenuItem>
              )}
              {!loadingBranches &&
                branches.map(b => (
                  <MenuItem key={b.name} value={b.name}>
                    {b.name}
                    {b.isDefault ? ' (padrão)' : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {/* AI Engine (D2/D-GHC-5) — opções montadas do catálogo do backend:
            engine sem credencial ou sem suporte do worker aparece VISÍVEL e
            desabilitada, com o motivo no texto de apoio. Sem catálogo
            (falha/carregando), todas habilitadas — comportamento anterior. */}
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.field}
          error={!!fieldErrors.aiEngine || noEngineAvailable}
        >
          <InputLabel id="create-ai-engine-label">Harness</InputLabel>
          <Select
            labelId="create-ai-engine-label"
            label="Harness"
            value={selectedAiEngine}
            onChange={e => {
              setSelectedAiEngine(e.target.value as AIEngineName);
              setFieldErrors(prev => ({ ...prev, aiEngine: '' }));
            }}
            data-testid="create-ai-engine-select"
          >
            {AI_ENGINE_OPTION_ORDER.map(engineName => (
              <MenuItem
                key={engineName}
                value={engineName}
                disabled={isEngineOptionDisabled(
                  aiEngines?.find(entry => entry.name === engineName),
                )}
              >
                {AI_ENGINE_OPTION_LABELS[engineName]}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {noEngineAvailable
              ? 'Nenhum harness disponível no worker. Instale um harness pelo aplicativo do worker (Desktop) ou configure a credencial que falta.'
              : fieldErrors.aiEngine ||
                (selectedAiEngine
                  ? engineHelperText(
                      selectedAiEngine,
                      aiEngines?.find(entry => entry.name === selectedAiEngine),
                    )
                  : 'Selecione o harness que executará esta atividade.')}
          </FormHelperText>
        </FormControl>

        {/* Title */}
        <TextField
          id="create-activity-title"
          label="Título"
          variant="outlined"
          size="small"
          fullWidth
          className={classes.field}
          value={title}
          onChange={e => {
            setTitle(e.target.value);
            setTitleEditedByUser(true);
            setFieldErrors(prev => ({ ...prev, title: '' }));
          }}
          error={!!fieldErrors.title}
          helperText={fieldErrors.title || undefined}
          inputProps={{ maxLength: TITLE_MAX_LENGTH }}
          InputLabelProps={{ shrink: true }}
        />

        {/* Description (visible when WI selected or blank mode, or agnostic flow). */}
        {(mode === 'blank' || selectedWorkItem || isAgnosticProvider) && (
          <TextField
            label="Descrição"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={4}
            className={classes.field}
            value={description}
            onChange={e => setDescription(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={
              selectedWorkItem
                ? 'Pré-preenchido do Work Item (editável)'
                : undefined
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            loadingProjects ||
            loadingRepos ||
            loadingBranches ||
            workerBlocksCreation
          }
        >
          Criar Atividade
        </Button>
      </DialogActions>
    </Dialog>
  );
};
